import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { MessageSquare, Mic, AlertCircle, MapPin, Calendar, IndianRupee, Play, Smartphone } from 'lucide-react';

const AIDoctorApp = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const features = [
    { icon: MessageSquare, text: "Natural language input", desc: "Text or voice symptom description" },
    { icon: AlertCircle, text: "AI E-triage classify", desc: "Mild/Moderate/Critical severity" },
    { icon: MapPin, text: "Smart routing", desc: "Right department & nearby facilities" },
    { icon: Calendar, text: "Book appointments", desc: "Doctor • Dentist • Nurse" },
    { icon: IndianRupee, text: "Freemium model", desc: "10 free → ₹29/₹49 subscription" },
    { icon: Play, text: "Rewarded ads", desc: "Watch ads to extend message limits" }
  ];

  return (
    <section id="ai-doctor" ref={ref} className="py-32 px-4 sm:px-6 lg:px-8 bg-black relative overflow-hidden">
      {/* Gradient Orb */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-blue-600/20 rounded-full blur-3xl" />
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-blue-400/10 rounded-full blur-3xl" />

      <div className="max-w-7xl mx-auto relative z-10">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Features Grid */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="space-y-4"
          >
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={isInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ delay: 0.2 + index * 0.08 }}
                  whileHover={{ 
                    scale: 1.03, 
                    x: -10,
                    rotateY: -5,
                    z: 30
                  }}
                  className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-4 sm:p-6 shadow-2xl hover:bg-white/10 transition-all"
                  style={{ transformStyle: "preserve-3d" }}
                >
                  <div className="flex items-start gap-4">
                    <div className="bg-blue-500/20 backdrop-blur-sm rounded-xl p-3 flex-shrink-0">
                      <Icon className="w-6 h-6 text-blue-400" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-white mb-1">{feature.text}</h3>
                      <p className="text-sm text-white/60">{feature.desc}</p>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </motion.div>

          {/* Content */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <motion.div
              className="inline-block mb-6"
              whileHover={{ scale: 1.05 }}
            >
              <div className="bg-blue-500/10 backdrop-blur-md border border-blue-500/20 rounded-full px-5 py-2">
                <span className="text-blue-400 font-semibold text-sm">Consumer Solution</span>
              </div>
            </motion.div>
            
            <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-7xl font-bold mb-6 text-white leading-tight">
              24/7 AI
              <br />
              <span className="text-blue-400">Doctor App</span>
            </h2>
            
            <p className="text-xl text-white/80 mb-4 font-medium">
              For Citizens
            </p>

            <p className="text-lg text-white/60 mb-12 leading-relaxed">
              Get instant medical triage in 3-8 seconds. Natural language symptom analysis with AI-powered department routing.
            </p>

            {/* Triage Speed Card */}
            <motion.div
              className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-3xl p-8 mb-12"
              whileHover={{ 
                scale: 1.02,
                rotateY: -5,
                z: 50
              }}
              transition={{ type: "spring", stiffness: 300 }}
              style={{ transformStyle: "preserve-3d" }}
            >
              <div className="flex items-center gap-6">
                <motion.div
                  animate={{ 
                    rotate: [0, 10, -10, 0],
                    scale: [1, 1.1, 1]
                  }}
                  transition={{ duration: 3, repeat: Infinity }}
                >
                  <Smartphone className="w-16 h-16 text-blue-400" />
                </motion.div>
                <div>
                  <p className="text-sm text-white/60 font-medium mb-2">AI Triage Speed</p>
                  <p className="text-5xl font-bold text-blue-400">3-8 sec</p>
                  <p className="text-xs text-white/50 mt-1">Instant medical guidance</p>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default AIDoctorApp;
